/**
 * Pulse — targets & calibration UI (docs/lifecycle-spine/PRD.md §6).
 *
 * The benchmark is a first-class, tunable, self-assessing object. Two surfaces
 * share one write seam (`pulse.setTarget`):
 *
 *   · <TargetChip>   — the per-bar/per-group chip (`target 70 · 64% attain` + a
 *                      calibration flag when the verdict ≠ well-set). Editors get
 *                      an inline-tune popover that nudges the target and
 *                      revalidates the wall live.
 *   · <TargetPanel>  — the Settings → <mode> → Targets panel: every bar with its
 *                      target · attainment · position · verdict · suggestion, an
 *                      accept-suggestion-in-one-click button, and a per-mode
 *                      count/value weight toggle.
 *
 * The calibration MATH is the engine's (`assess`, surfaced per touch as
 * `calibration`); this block SURFACES it — it never recomputes a second verdict.
 * Writes go through `apiCall(pulse.setTarget)`; reads revalidate via
 * `useSWRConfig().mutate(apiKey(pulse.aggregate|breakdown, …))` (the shell mounts
 * a custom SWR provider, so the bare `swr` `mutate` is a no-op).
 */

import React, { useState, useTransition } from "react";
import { useSWRConfig } from "swr";
import { Button } from "../../button";
import { Input } from "../../input";
import { Icon } from "../../icon";
import { Popover, PopoverContent, PopoverTrigger } from "../../popover";
import { IntentBadge } from "../status-badge";
import { SettingsSection } from "../settings/settings-section";
import { useHydrated } from "../../use-hydrated";
import { apiCall, apiKey, useApi } from "../../api/client";
import {
  pulse,
  type PulseGroupDTO,
  type PulseTouchDTO,
  type PulseCalibrationDTO,
  type PulseWeight,
} from "../../api/contracts/pulse";

// ── trend arrow (§6.2 — the calibration arrow) ────────────────────────────────

/** The range-over-range trend direction carried on each bar (§6.2). */
export type PulseTrend = NonNullable<PulseTouchDTO["trend"]>;

const TREND_META: Record<PulseTrend, { icon: string; cls: string; title: string }> = {
  up: { icon: "arrow-up", cls: "text-success", title: "improving vs the prior window" },
  flat: { icon: "arrow-right", cls: "text-muted-foreground/60", title: "flat vs the prior window" },
  down: { icon: "arrow-down", cls: "text-danger", title: "regressing vs the prior window" },
};

/** The trend arrow (↑/→/↓). Renders nothing when the trend is unknown (a ghost
 *  on either side of the range-over-range diff). */
export function TrendArrow({ trend, className }: { trend?: PulseTrend; className?: string }) {
  if (!trend) return null;
  const m = TREND_META[trend];
  return (
    <span title={m.title} className="inline-flex">
      <Icon name={m.icon} size="xs" className={`${m.cls} ${className ?? ""}`} />
    </span>
  );
}

// ── calibration → presentation (the verdict tone + label; §6.2) ───────────────

type Verdict = PulseCalibrationDTO["verdict"];

const VERDICT_META: Record<
  Verdict,
  { label: string; intent: "success" | "warning" | "info" | "neutral"; tune: boolean }
> = {
  "well-set": { label: "well-set", intent: "success", tune: false },
  "too-loose": { label: "too loose", intent: "warning", tune: true },
  "too-strict": { label: "too strict", intent: "warning", tune: true },
  "no-signal": { label: "no signal", intent: "neutral", tune: false },
};

function pct(n: number): number {
  return Math.round(n * 100);
}

// ── TargetChip — the per-bar/per-group benchmark chip + inline tune ────────────

export interface TargetChipProps {
  /** Mode slug — the write key prefix (`pulse.target.<mode>.…`). */
  mode: string;
  /** The bar's group key + touch id (the write address). Omit for a group-level
   *  read-only chip (no single bar to tune). */
  groupKey?: string;
  touchId?: string;
  /** The resolved target (engine-banded). */
  target: number;
  /** The calibration assessment the engine returns per touch (§6.2). */
  calibration?: PulseCalibrationDTO;
  /** The range-over-range trend (§6.2 — the arrow). Omitted ⇒ no arrow. */
  trend?: PulseTrend;
  /** The scope key the wall reads under — revalidated after a write. */
  scope: string;
  /** Editors get the inline-tune popover; otherwise the chip is read-only. */
  editable?: boolean;
  className?: string;
}

/**
 * `target 70 · 64% attain` + a calibration flag when the verdict is not
 * `well-set`. When `editable` and a (groupKey, touchId) are present, clicking the
 * chip opens a popover to nudge the target (writes `pulse.setTarget`, revalidates).
 */
export function TargetChip({
  mode,
  groupKey,
  touchId,
  target,
  calibration,
  trend,
  scope,
  editable,
  className,
}: TargetChipProps) {
  const attain = calibration ? `${pct(calibration.attainment)}% attain` : null;
  const verdict = calibration?.verdict;
  const flag = verdict && VERDICT_META[verdict].tune ? VERDICT_META[verdict] : null;

  // `target 70 · 64% attain · ↑` (§6.3) — the trend arrow lands on the chip.
  const label = (
    <span className="flex items-center gap-1.5 font-mono type-subtext tabular-nums text-muted-foreground/70">
      <span>
        target {target}
        {attain ? ` · ${attain}` : ""}
      </span>
      <TrendArrow trend={trend} />
      {flag && <IntentBadge intent={flag.intent} className="px-1 py-0 type-subtext">{flag.label}</IntentBadge>}
    </span>
  );

  // A non-editable chip, or a group-level chip (no single bar to write), is a
  // plain read-only label.
  if (!editable || !groupKey || !touchId) {
    return <span className={className}>{label}</span>;
  }

  return (
    <TargetTunePopover
      mode={mode}
      groupKey={groupKey}
      touchId={touchId}
      target={target}
      calibration={calibration}
      scope={scope}
    >
      <button
        type="button"
        className={`rounded-sm px-1 py-0.5 transition-colors hover:bg-muted/60 ${className ?? ""}`}
        title="Tune this target"
      >
        {label}
      </button>
    </TargetTunePopover>
  );
}

/** The inline-tune popover — nudge a bar's target + (optional) rationale. */
function TargetTunePopover({
  mode,
  groupKey,
  touchId,
  target,
  calibration,
  scope,
  children,
}: {
  mode: string;
  groupKey: string;
  touchId: string;
  target: number;
  calibration?: PulseCalibrationDTO;
  scope: string;
  children: React.ReactNode;
}) {
  const { mutate } = useSWRConfig();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(String(target));
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  const save = (next: number) => {
    setError(null);
    startTransition(async () => {
      try {
        await apiCall(pulse.setTarget, {
          body: { mode, groupKey, touchId, target: next },
        });
        await mutate(apiKey(pulse.aggregate, { params: { scope }, query: {} }));
        await mutate(apiKey(pulse.breakdown, { params: { scope, touch: touchId }, query: {} }));
        setOpen(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Couldn't save.");
      }
    });
  };

  const suggestion = calibration?.suggestion;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setValue(String(target));
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" className="w-64 space-y-3">
        <div className="space-y-1">
          <span className="type-body font-medium">Tune target</span>
          {calibration && (
            <p className="type-subtext text-muted-foreground">
              {pct(calibration.attainment)}% of {calibration.sample}{" "}
              {calibration.sample === 1 ? "subject" : "subjects"} pass ·{" "}
              {VERDICT_META[calibration.verdict].label}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={0}
            max={100}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="h-7 w-20"
          />
          <Button
            size="sm"
            disabled={pending || value === "" || Number(value) === target}
            onClick={() => {
              const n = Math.max(0, Math.min(100, Math.round(Number(value))));
              if (Number.isFinite(n)) save(n);
            }}
          >
            Save
          </Button>
        </div>
        {suggestion && (
          <Button
            variant="outline"
            size="sm"
            disabled={pending}
            onClick={() => save(suggestion.target)}
            className="w-full justify-start"
            title={suggestion.why}
          >
            <Icon name="sparkles" size="xs" className="text-warning" />
            <span className="flex-1 text-left">
              Use suggested {suggestion.target}{" "}
              <span className="text-muted-foreground/70">({suggestion.confidence})</span>
            </span>
          </Button>
        )}
        {error && <p className="type-subtext text-destructive">{error}</p>}
      </PopoverContent>
    </Popover>
  );
}

// ── TargetPanel — the Settings → <mode> → Targets panel (§6.3) ────────────────

export interface TargetPanelProps {
  /** The mode whose wall's targets this panel tunes. */
  mode: string;
  /** The scope key the wall reads under (`mode` for the full wall). Defaults to
   *  `mode`; revalidated after every write. */
  scope?: string;
  className?: string;
}

/**
 * The per-mode Targets panel: lists every live bar with target · attainment ·
 * position · verdict · suggestion (accept in one click), plus the per-mode
 * count/value weight toggle. Reads the mode's wall (`pulse.aggregate`) and writes
 * through `pulse.setTarget`. Always editable — it is a Settings surface.
 */
export function TargetPanel({ mode, scope, className }: TargetPanelProps) {
  const wallScope = scope ?? mode;
  // The panel reads its own copy of the wall (SWR shares the cache key with the
  // wall it tunes, so an inline edit revalidates both).
  const { data, isLoading, error } = useApi(pulse.aggregate, {
    params: { scope: wallScope },
    query: {},
  });
  // The SWR cache is hydrated from localStorage on the client's first render, so
  // this persisted bare key returns `data` on render #0 while the server (empty
  // cache) showed the loading line — a hydration mismatch. Hold the loading state
  // until hydrated so render #0 matches the server (see useHydrated).
  const hydrated = useHydrated();

  const groups = (data?.groups ?? []).filter((g) => g.touches.some((t) => t.health != null));

  return (
    <SettingsSection
      title="Targets"
      description="Tune each benchmark and let the wall tell you whether it's set right. A target that almost everyone passes (too loose) or almost no one passes (too strict) is flagged with a one-click suggestion."
      className={className}
      actions={<WeightToggle mode={mode} scope={wallScope} weight={undefined} />}
    >
      {!hydrated || (isLoading && !data) ? (
        <div className="type-body text-muted-foreground">Loading the wall…</div>
      ) : error ? (
        <div className="type-body text-destructive">Couldn&rsquo;t load the wall.</div>
      ) : groups.length === 0 ? (
        <div className="type-body text-muted-foreground">
          No instrument is live for this mode yet — there&rsquo;s nothing to tune.
        </div>
      ) : (
        <div className="flex flex-col">
          {groups.map((g) => (
            <TargetGroupBlock key={g.key} mode={mode} scope={wallScope} group={g} />
          ))}
        </div>
      )}
    </SettingsSection>
  );
}

function TargetGroupBlock({
  mode,
  scope,
  group,
}: {
  mode: string;
  scope: string;
  group: PulseGroupDTO;
}) {
  const live = group.touches.filter((t) => t.health != null);
  if (!live.length) return null;
  return (
    <div className="border-b border-border/40 py-3 first:pt-0 last:border-b-0">
      <div className="mb-1.5 flex items-center gap-1.5">
        <Icon name={group.icon} size="xs" className="text-muted-foreground" />
        <span className="font-mono type-subtext uppercase tracking-wider text-muted-foreground">
          {group.label}
        </span>
      </div>
      <div className="flex flex-col">
        {live.map((t) => (
          <TargetRow key={t.id} mode={mode} scope={scope} groupKey={group.key} touch={t} />
        ))}
      </div>
    </div>
  );
}

function TargetRow({
  mode,
  scope,
  groupKey,
  touch,
}: {
  mode: string;
  scope: string;
  groupKey: string;
  touch: PulseTouchDTO;
}) {
  const { mutate } = useSWRConfig();
  const [value, setValue] = useState(String(touch.target));
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const cal = touch.calibration;

  const revalidate = async () => {
    await mutate(apiKey(pulse.aggregate, { params: { scope }, query: {} }));
    await mutate(apiKey(pulse.breakdown, { params: { scope, touch: touch.id }, query: {} }));
  };

  const save = (next: number) => {
    setError(null);
    startTransition(async () => {
      try {
        await apiCall(pulse.setTarget, { body: { mode, groupKey, touchId: touch.id, target: next } });
        setValue(String(next));
        await revalidate();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Couldn't save.");
      }
    });
  };

  const verdict = cal?.verdict;
  const flag = verdict ? VERDICT_META[verdict] : null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border/40 py-2 first:pt-1 last:border-b-0">
      <span className="w-32 shrink-0 truncate type-body">{touch.label}</span>

      <Input
        type="number"
        min={0}
        max={100}
        value={value}
        disabled={pending}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          const n = Math.max(0, Math.min(100, Math.round(Number(value))));
          if (Number.isFinite(n) && n !== touch.target) save(n);
        }}
        className="h-7 w-16"
      />

      {cal ? (
        <span className="flex items-center gap-2 font-mono type-subtext tabular-nums text-muted-foreground">
          <span>{pct(cal.attainment)}% attain</span>
          <span className="text-muted-foreground/60">pos {pct(cal.position)}</span>
          {flag && (
            <IntentBadge intent={flag.intent} className="px-1 py-0 type-subtext">
              {flag.label}
            </IntentBadge>
          )}
        </span>
      ) : (
        <span className="font-mono type-subtext text-muted-foreground/60">no signal</span>
      )}

      {cal?.suggestion && (
        <Button
          size="xs"
          variant="outline"
          disabled={pending}
          onClick={() => save(cal.suggestion!.target)}
          title={cal.suggestion.why}
        >
          <Icon name="sparkles" size="xs" />
          Use {cal.suggestion.target}
        </Button>
      )}

      {error && <span className="type-subtext text-destructive">{error}</span>}
    </div>
  );
}

// ── value/count weight toggle (the wall + panel control; §5/§6) ───────────────

export interface WeightToggleProps {
  mode: string;
  /** The scope key the wall reads under — revalidated after the flip. */
  scope: string;
  /** The current effective weight (when known) to reflect the active state. */
  weight?: PulseWeight;
  className?: string;
}

/**
 * Flip the per-mode aggregation weight (count ↔ value). Writes
 * `pulse.weight.<mode>` via `pulse.setTarget` and revalidates the wall so it
 * re-bands live (§5 weighting). Small inline segmented control.
 */
export function WeightToggle({ mode, scope, weight, className }: WeightToggleProps) {
  const { mutate } = useSWRConfig();
  const [active, setActive] = useState<PulseWeight | undefined>(weight);
  const [pending, startTransition] = useTransition();

  const set = (w: PulseWeight) => {
    if (pending || active === w) return;
    startTransition(async () => {
      try {
        await apiCall(pulse.setTarget, { body: { mode, weight: w } });
        setActive(w);
        await mutate(apiKey(pulse.aggregate, { params: { scope }, query: {} }));
      } catch {
        /* leave the prior state; the toggle is non-destructive */
      }
    });
  };

  const opt = (w: PulseWeight, label: string) => (
    <button
      type="button"
      disabled={pending}
      aria-pressed={active === w}
      onClick={() => set(w)}
      className={`rounded-sm px-2 py-0.5 type-subtext transition-colors ${
        active === w
          ? "bg-selected text-foreground"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div
      className={`flex items-center gap-0.5 rounded-md border border-border/60 p-0.5 ${className ?? ""}`}
      title="Weight each subject equally (count) or by its value, e.g. ARR (value)"
    >
      {opt("count", "Count")}
      {opt("value", "Value")}
    </div>
  );
}
